import { Influencer, Post, SocialAccount } from "types/influencer"
import { CampaignApplication } from "../applications"
import { Campaign } from ".."
import { CampaignGig, CampaignGigOption } from "../milestones"

export interface Collaborations {
    count: number,
    items: Collaboration[]
}

export interface Collaboration {
    uuid: string,
    status: string,
    total_earnings: string | null,
    influencer?: Influencer,
    posts: CollaborationPost[],
    approved_posts_count: 0,
    views_count: number,
    interactions_count: number,
    cpe: string,
    cpv: string,
    clicks_count?: number,
    application: CampaignApplication,
    active_milestone: Milestone,
    active_steps: MilestoneStep[],
    campaign: Pick<Campaign, "uuid" | "name" | "platform">,
    language?: {name: string},
    avg_watch_time?: number,
    total_watch_time?: string,
    platform: string,
    social_account: SocialAccount,
}

export interface Milestone {
    id: number,
    sort_order: number,
    campaign_gigs: any[],
    gig_type: GigType,
    gig_type_id: number,
    campaign_gig_ids: number[],
    maximum_gigs: number,
    content_media_types: {
        REELS: string | number,
        FEED: string | number,
        STORY: string | number,
        VIDEO?: string | number
    },
    specifications: MilestonSpecifications,
    brief: string,
    starts_at: string | null,
    ended_at: string | null,
    is_active: boolean,
    gig_amount: string | number,
    barter_amount: string | number,
    barter_reimbursement?: boolean,
    campaign_files: CampaignFile[],
    acknowledged_at?: string,
    feedbacks: MilestoneStepFeedback[]
    files: MilestoneFile[],
    [key: string]: any
}



export interface CollaborationsSlice {
    error: string | null,
    loading: boolean,
    collaborationError: string | null,
    collaborationLoading: boolean,
    campaigns: Record<string, {
        total: number,
        filtered_lists: Record<string, {
            count: number,
            list: Collaboration[],
            page: number,
            has_more: boolean,
            last_fetched_at: number,    
            filters: any,
            expired: boolean
        }>
        collaborations: Record<string, {
            collaboration: Collaboration,
            milestones: Milestone[]
            chat: {
                uuid: string
            } | null,
            last_fetched_at: number,
        }>,
        basic_collaborations: Record<string, { // collaborationId not influencerId
            collaboration: Collaboration,
            last_fetched_at: number,
        }>
    }>,
    applied_filter_id: string | null,
    filters: CampaignCollaborationsFilters,
    tableFilters: CampaignCollaborationsFilters,
    collaborations : {
        defaultFilters: MilestoneCollaborationsFilters,
        applied_filter_id: string | null,
        total: number,
        filtered_lists: Record<string, {
            list: CollaborationItem[],
            count: number,
            page: number,
            last_fetched_at: number | null,
            expired: boolean,
            has_more: boolean
        }>
        loading: boolean,
        error: string | null,
        gigSteps: any[]
    },
    pageLoading: boolean,
    collaborationPosts: Record<string, Record<string, {social_post: SocialPost, media_url: string, last_fetched_at: number}>>,
    collaborationPostLoading: boolean,
}

export interface CollaborationItem {
    uuid: string,
    created: string,
    last_activity: string,
    influencer?: Influencer,
    social_account: SocialAccount,
    campaign: Pick<Campaign, "uuid" | "name">,
    active_milestone: Milestone,
    active_steps: MilestoneStep[],
    milestones_count: number
}

export enum SortOptions {
    MOST_RECENT = 'id',
    MOST_INTERACTIONS = 'interactions_count',
    MOST_VIEWS = 'views_count',
    LOWEST_CPV = 'cpv',
    LOWEST_CPE = 'cpe',
    HIGHEST_CLICKS = 'clicks_count',
    HIGHEST_WATCH_TIME='total_watch_time'
}

export const SortOptionLabel = {
    [SortOptions.MOST_RECENT]: 'Most Recent',
    [SortOptions.MOST_INTERACTIONS]: 'Most Interactions',
    [SortOptions.MOST_VIEWS]: 'Most Views',
    [SortOptions.LOWEST_CPV]: 'Lowest CPV',
    [SortOptions.LOWEST_CPE]: 'Lowest CPE',
    [SortOptions.HIGHEST_CLICKS]: 'Most Clicks',
    [SortOptions.HIGHEST_WATCH_TIME]: 'Total Watch Time'
}

export interface CampaignCollaborationsFilters {
    page: number,
    sort: SortOptions,
    order?: 'asc' | 'desc',
    q?: string,
    [key: string]: any
}  



export interface MilestonSpecifications {
    do: string[],
    dont: string[],
    talking_points?: TalkingPoint[],
    hashtags?: string[],
    mentions?: string[],
    collaborators?: string[],
    sample_scripts?: string[],
    sample_captions?: string[],
    [key: string]: any
}

export interface CampaignFile {
    uuid: string,
    type: string,
    url: string,
    [key: string] : any
}

export interface TalkingPoint {
    title: string,
    pointers: string[]
}

export interface GigType {
    id: number,
    slug: string,
    name: string,
    object_name: string,
    properties: GigTypeProperty[],
    has_value?: boolean,
    has_gigs?: boolean,
    has_options?: boolean,
    description?: string
}

export interface GigTypeProperty {
    id: number,
    gig_type_id: number,
    label: string,
    key: string,
    type: string,
    sort_order: number,
    mandatory: boolean,
    description?: string | null,
    choices?: {
        [key: string]: string
    }
}

export interface MilestoneStep {    
    sort_order: number,
    is_locked: boolean,
    is_processed: boolean,
    processed: any,
    deliverables: {
        [key: string]: string
    },
    can_process: boolean,
    gig_step: GigStep,
    deadline: string | null
    acknowledged_at?: string,
    assignees?: string[],
    [key: string]: any
}

export interface MilestoneFile {
    step: string,
    uuid: string,
    url: string,
    type: string
}

export interface MilestoneStepFeedback {
    step: string,
    text: string,
    revision_required: boolean,
    created_at: string
    reaction?: 'good' | 'better' | 'excellent'
}

export interface GigStep {
    slug: string,
    name: string,
    title: string,
    description: string,
    cta_text: string,
    revision_required_title: string,
    processed_title: string,
    object_name: string,
    properties: GigStepProperty[],
    attributes: Record<string, any>,
    require_deadline: boolean,
    non_assignee_title?: string | null
    require_acknowledgement?: boolean,
    help_url?: string | null,
}

export interface GigStepProperty {
    label: string,
    key: string,
    type: 'boolean' | 'file' | 'large_file' | 'text' | 'textarea' | 'choice' | 'number' | 'wysiwyg' | 'address' | 'approval' | 'date' | 'datetime' | 'caption' | 'post' | 'permalink' | 'url' | 'tel',
    sort_order: number,
    mandatory: boolean,
    choices: {
        property: string
    },
    attributes: {
        max_file_size?: number,
        min_file_size?: number,
        accepted?: string[],
        [key: string] : any
    },
    description?: string | null

}

export interface GigStepPropertyWithValue extends GigStepProperty {
    value?: string | boolean | File | CTA,
    error?: string
}


export interface MilestoneData {
    milestone: Milestone,
    steps: MilestoneStep[],
    active_steps: MilestoneStep[],
    events: MilestoneEvent[]
    gigs: Gig[],
    posts: CollaborationPost[]
}

export interface MilestoneEvent {
    created: string,
    created_at: string,
    step: MilestoneStep,
    revision_required: boolean
    deliverables: any
    feedback?: MilestoneStepFeedback | null
}
export interface Gig {
    id: number,
    name: string,
    properties: Record<string, any>,
    value?: string
    thumbnail_url?: string,
    options?: CampaignGigOption[]
}


export interface CollaborationPost {
    status: "approved" | "rejected" | "pending",
    ig_post: Post,
    social_post: SocialPost
}

export interface SocialPost {
    uuid: string,
    platform: string,
    engagement_score?: string,
    posted_on?: string,

    thumbnail_url: string,
    like_count: number,
    comments_count: number,
    views_count: number,
    type: 'STORY' | 'FEED' | 'REELS' | 'VIDEO',
    ig?: SocialPostIG,
    social_account?: SocialAccount,
    speciality_niche_tier1?: string | null,
    collab?: SocialPostCollab,
    [key: string]: any
}

export interface SocialPostCollab {
    handle: string,
}

export interface BasicSocialPost {
    uuid: string, 
    social_account: {
        uuid: string
    },
    thumbnail_url: string
}

export interface SocialPostIG {
    permalink: string
    caption?: string | null
    like_count: number
    comments_count: number
    reach_count?: number | null
    shares_count?: number | null
    views_count?: number
    media_product_type?: 'REELS' | 'FEED' | 'STORY' | string
    media_type?: 'VIDEO' | 'IMAGE' | 'CAROUSEL_ALBUM' | string
    media_url?: string
    posted_on?: string
    [key: string]: any
}

export interface ProcessMilestonePayload {
    step: string, 
    approval?: 'yes' | 'no'
    action?: 'accept' | 'reject', 
    [key: string]: any    
}

export interface MilestoneCollaborationsFilters {
    page: number,
    sort: MilestoneCollaborationSortOptions,
    status?: MilestoneCollaborationStatus,
    assigned_self?: 'y' | 'n',
    q?: string,
    [key: string]: any
}

export enum MilestoneCollaborationSortOptions {
    START_DATE = 'created_at',
    LAST_ACTIVITY_AT = 'last_activity_at'
}


export enum MilestoneCollaborationStatus {
    ONGOING='ongoing',
    COMPLETED='complete'
}

export const MilestoneCollaborationStatusLabel = {
    [MilestoneCollaborationStatus.ONGOING]: "Ongoing",
    [MilestoneCollaborationStatus.COMPLETED]: "Completed",
}

export const MilestoneCollaborationSortOptionLabel = {
    [MilestoneCollaborationSortOptions.START_DATE]: 'Start Date',
    [MilestoneCollaborationSortOptions.LAST_ACTIVITY_AT]: 'Last Activity'
}

export interface CTA {
    text: string,
    url?: string | null,
    unsaved?: boolean
}

export interface CollaborationTasksSummary {
    steps: {
        [key: string]: {
            name: string,
            count: number
        }
    }
}


export enum MilestoneStepAssignee {
    AGENCY = 'agency',
    INFLUENCER = 'influencer',
    HUB = 'hub',

}

export const MilestoneStepAssigneeLabel = {
    [MilestoneStepAssignee.HUB]: 'Influns',
    [MilestoneStepAssignee.INFLUENCER]: 'Creator',
    [MilestoneStepAssignee.AGENCY]: 'Me'
}

export const MilestoneStepAssigneeColorCode = {
    [MilestoneStepAssignee.AGENCY]: 'success',
    [MilestoneStepAssignee.HUB]: 'dark-subtle',
    [MilestoneStepAssignee.INFLUENCER]: 'warning'
}

export interface BulkProcessCollab {
    influencer?: {
        name: string,
        uuid: string,
        ig_account: {
            username: string,
        },
        social_account: {
            platform: string,
            handle: string
        }
    },
    collaboration: {
        uuid: string,
    },
    social_account: SocialAccount,
    gig_type: GigType,
    campaign_gigs: CampaignGig[],
    processed_steps: MilestoneStep[],
    pending_steps: MilestoneStep[]
}